import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiRequest } from "../../../shared/api/client";
import type { SessionUser } from "../../../shared/auth/access";
import type { Task, Notification, Metrics } from "./warehouse-types";
import { desktopNotificationState, unseenOpenNotificationIds } from "../desktop-action-notifications";
import { playDesktopActionChime } from "../desktop-action-sound";
import { visibleWarehousePreparationTasks, groupWarehousePreparationTasks, preparationNotificationAction } from "../warehouse-preparation-view";
import { warehouseRoleCopy } from "./warehouse-presentation";
export type WarehousePreparationProps = { user: SessionUser; notify: (message: string) => void; dataRefreshVersion: number };
const desktopNotificationStorageKey = "anl-material-preparation-desktop-notifications";
export function useWarehousePreparationWorkspace({ user, notify, dataRefreshVersion }: WarehousePreparationProps) {
  const workerRole =
    user.role === "DEPO_GOREVLISI" || user.role === "CEP_DEPO_SORUMLUSU";
  const [tasks, setTasks] = useState<Task[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [desktopPermission, setDesktopPermission] = useState<
    NotificationPermission | "unsupported"
  >(() =>
    typeof window !== "undefined" && "Notification" in window
      ? window.Notification.permission
      : "unsupported",
  );
  const [desktopEnabled, setDesktopEnabled] = useState(() =>
    typeof window !== "undefined"
      ? window.localStorage.getItem(desktopNotificationStorageKey) === "true"
      : false,
  );
  const knownNotificationIds = useRef<Set<string> | null>(null);
  const load = useCallback(async (background = false) => {
    if (!background) setLoading(true);
    setError(null);
    try {
      const [taskResult, notificationResult, metricResult] = await Promise.all([
        apiRequest<{ items: Task[] }>(
          "/api/material-preparation/tasks?completed=true",
          { cache: "no-store" },
        ),
        apiRequest<{ items: Notification[] }>(
          "/api/material-preparation/notifications",
          { cache: "no-store" },
        ),
        workerRole
          ? Promise.resolve(null)
          : apiRequest<Metrics>("/api/material-preparation/metrics", {
              cache: "no-store",
            }),
      ]);
      setTasks(taskResult.items);
      setNotifications(notificationResult.items);
      setMetrics(metricResult);
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Hazırlık görevleri alınamadı.",
      );
    } finally {
      if (!background) setLoading(false);
    }
  }, [workerRole]);
  useEffect(() => {
    void load();
  }, [load, dataRefreshVersion]);
  useEffect(() => {
    if (user.role !== "SISTEM_YONETICISI") return;
    const timer = window.setInterval(() => void load(true), 30_000);
    return () => window.clearInterval(timer);
  }, [load, user.role]);
  const visibleTasks = useMemo(
    () => visibleWarehousePreparationTasks(tasks, user.role),
    [tasks, user.role],
  );
  const groups = useMemo(
    () => groupWarehousePreparationTasks(visibleTasks),
    [visibleTasks],
  );
  const orderedGroups = useMemo(() => {
    const order =
      user.role === "CEP_DEPO_SORUMLUSU"
        ? ["critical", "confirmation", "work", "upcoming", "ready"]
        : ["critical", "work", "upcoming", "confirmation", "ready"];
    return [...groups].sort(
      (left, right) => order.indexOf(left.key) - order.indexOf(right.key),
    );
  }, [groups, user.role]);
  const activeTaskCount = visibleTasks.filter(
    (task) => !["READY", "SUPERSEDED", "EXCEPTION_CLOSED"].includes(task.status),
  ).length;
  const urgentTaskCount = groups.find((group) => group.key === "critical")
    ?.items.length ?? 0;
  const completedTaskCount = visibleTasks.filter((task) =>
    ["READY", "EXCEPTION_CLOSED"].includes(task.status),
  ).length;
  const roleCopy = warehouseRoleCopy(user.role);
  const visibleTaskIds = new Set(visibleTasks.map((task) => task.id));
  const openNotifications = notifications.filter(
    (item) =>
      item.status === "OPEN" &&
      (!workerRole || (item.taskId !== null && visibleTaskIds.has(item.taskId))),
  );
  const desktopState = desktopNotificationState(
    desktopPermission !== "unsupported",
    desktopPermission === "unsupported" ? "default" : desktopPermission,
    desktopEnabled,
  );
  const showDesktopNotification = useCallback(
    (title: string, message: string, tag: string) => {
      if (
        typeof window === "undefined" ||
        !("Notification" in window) ||
        window.Notification.permission !== "granted"
      )
        return;
      const desktopNotification = new window.Notification(title, {
        body: message,
        icon: "/anl-logo-new-2026.webp",
        silent: false,
        tag,
      });
      desktopNotification.onclick = () => {
        window.focus();
        desktopNotification.close();
      };
    },
    [],
  );
  useEffect(() => {
    if (loading) return;
    const currentIds = new Set(openNotifications.map((item) => item.id));
    if (knownNotificationIds.current === null) {
      knownNotificationIds.current = currentIds;
      return;
    }
    const newIds = unseenOpenNotificationIds(
      openNotifications,
      knownNotificationIds.current,
    );
    knownNotificationIds.current = currentIds;
    if (user.role !== "SISTEM_YONETICISI" || desktopState !== "ON") return;
    for (const id of newIds) {
      const item = openNotifications.find((candidate) => candidate.id === id);
      if (item)
        showDesktopNotification(
          item.title,
          item.message,
          `material-preparation-${item.id}`,
        );
    }
    if (newIds.length) void playDesktopActionChime();
  }, [
    desktopState,
    loading,
    openNotifications,
    showDesktopNotification,
    user.role,
  ]);
  const enableDesktopNotifications = async () => {
    if (desktopPermission === "unsupported") return;
    const permission = await window.Notification.requestPermission();
    setDesktopPermission(permission);
    if (permission !== "granted") {
      setDesktopEnabled(false);
      window.localStorage.removeItem(desktopNotificationStorageKey);
      notify("Masaüstü bildirim izni verilmedi.");
      return;
    }
    window.localStorage.setItem(desktopNotificationStorageKey, "true");
    setDesktopEnabled(true);
    showDesktopNotification(
      "ANL Tedarik bildirimleri açık",
      "Yeni aksiyonlar bu bilgisayarda sistem bildirimi olarak gösterilecek.",
      "material-preparation-test",
    );
    const soundPlayed = await playDesktopActionChime();
    notify(
      soundPlayed
        ? "Masaüstü bildirimleri açıldı; sesli test gönderildi."
        : "Bildirim açıldı ancak tarayıcı uyarı sesini başlatamadı.",
    );
  };
  const testDesktopNotifications = async () => {
    showDesktopNotification(
      "ANL Tedarik test bildirimi",
      "Masaüstü aksiyon bildirimi çalışıyor.",
      `material-preparation-test-${Date.now()}`,
    );
    const soundPlayed = await playDesktopActionChime();
    notify(
      soundPlayed
        ? "Sesli masaüstü test bildirimi gönderildi."
        : "Sistem bildirimi gönderildi ancak uygulama sesi çalınamadı.",
    );
  };
  const disableDesktopNotifications = () => {
    window.localStorage.removeItem(desktopNotificationStorageKey);
    setDesktopEnabled(false);
    notify("Masaüstü bildirimleri bu bilgisayarda kapatıldı.");
  };
  const openNotification = async (item: Notification) => {
    const action = preparationNotificationAction(item);
    if (item.status === "OPEN") {
      try {
        const next = await apiRequest<Notification>(
          `/api/material-preparation/notifications/${item.id}/read`,
          { method: "POST" },
        );
        setNotifications((current) =>
          current.map((candidate) =>
            candidate.id === next.id ? next : candidate,
          ),
        );
      } catch (error) {
        notify(
          error instanceof Error ? error.message : "Bildirim güncellenemedi.",
        );
      }
    }
    if (action.targetId)
      requestAnimationFrame(() => {
        const target = document.getElementById(action.targetId!);
        if (!target) {
          notify(
            "İlgili kayıt açık aksiyon görünümünde değil. Güncel durumu görmek için listeyi yenileyin.",
          );
          return;
        }
        target.focus({ preventScroll: true });
        target.scrollIntoView({ behavior: "smooth", block: "center" });
      });
  };
  return { workerRole, loading, error, metrics, desktopState, roleCopy,
    activeTaskCount, urgentTaskCount, completedTaskCount, openNotifications,
    orderedGroups, load, setTasks, testDesktopNotifications,
    enableDesktopNotifications, disableDesktopNotifications, openNotification };
}
